import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import DraggableVideoPlayer from "@/components/draggable_video_player";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { trackEvent, ANALYTICS_EVENTS } from "@/lib/analytics";
import { useBrandConfig } from "@/hooks/use-brand-config";
import { useAccounts } from "@/contexts/account-context";
import {
  HelpingHand,
  Lightbulb,
  BarChart3,
  GraduationCap,
  ArrowRight,
  Zap,
  TrendingUp,
  Search,
  Map,
  Clock,
  Target,
  Package,
  Play,
  Book,
} from "lucide-react";

export default function HelpPage() {
  const { brandConfig } = useBrandConfig();
  const { accounts, activeDexieId } = useAccounts();

  // Get the current active account
  const currentAccount = accounts.find(
    (account) => account.dexieId === activeDexieId,
  );

  // Check if sidebar should be shown (hide for white-label clients)
  const showSidebar =
    brandConfig.features.salesPages && brandConfig.features.learningContent;

  // Video data for each page guide
  const pageVideos = {
    products: [
      {
        id: "products-1",
        title: "Products Analysis Tutorial",
        loomId: "14551f7ee02a436f976624ce379835ff",
      },
    ],
    analysis: [
      {
        id: "analysis-1",
        title: "Budget Analysis Tutorial",
        loomId: "43d90263964741ba9eb9153854247b4f",
      },
    ],
    trends: [
      {
        id: "trends-1",
        title: "Trends & PMax Channels Tutorial",
        loomId: "12422a4b784b42de8532cc3f3391884d",
      },
    ],
    keywords: [
      {
        id: "keywords-1",
        title: "Keywords & Search Terms Tutorial",
        loomId: "599eda31899a44ddaf2ea56167bb1d87",
      },
    ],
    placements: [
      {
        id: "placements-1",
        title: "PMax Placements Tutorial",
        loomId: "3d6b73393d4e4afe91f37db1c4771c97",
      },
    ],
    curve: [
      {
        id: "curve-1",
        title: "Profit Curve Tutorial",
        loomId: "c7517bf2dfbd4ad39494b1a54fd31dc3",
      },
    ],
    hourly: [
      {
        id: "hourly-1",
        title: "Hourly Trends Tutorial",
        loomId: "e9c3bcf2a631482bb587dd0869855ed7",
      },
    ],
    assets: [
      {
        id: "assets-1",
        title: "Asset Performance Tutorial",
        loomId: "76f502ca5e7a4df9970254d1290a1c46",
      },
    ],
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div
        className={`flex flex-col ${showSidebar ? "lg:flex-row" : ""} gap-12`}
      >
        {/* Main Content Area */}
        <div className={showSidebar ? "lg:w-3/4" : "w-full"}>
          <div className="mb-6">
            <h1 className="text-3xl font-bold">Help & Support</h1>
          </div>

          {/* Introduction Section */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Introduction</h2>
            <p className="text-lg">
              Welcome to {brandConfig.appName}! This application helps you
              optimize your Google Ads campaigns by providing insights and
              recommendations based on your performance data.
            </p>
          </section>

          {/* Getting Started Guide */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Getting Started</h2>
            <ol className="list-decimal list-inside space-y-1 text-lg">
              <li>
                Create an account or sign in to access the full features of the
                app.
              </li>
              <li>
                Configure your Google Ads integration in the{" "}
                <Link to="/settings" className="text-primary hover:underline">
                  Settings
                </Link>{" "}
                page.
              </li>
              <li>
                Start analyzing your campaign data on the{" "}
                <Link to="/trends" className="text-primary hover:underline">
                  Trends
                </Link>{" "}
                page.
              </li>
            </ol>
          </section>

          {/* Detailed Page Guides */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Page Guides</h2>

            {/* Two-column grid layout for page guides */}
            <div className="grid gap-6 md:grid-cols-2">
              {/* Products Page Guide - Hide for leadgen businesses */}
              {!(
                currentAccount && currentAccount.businessMode === "leadgen"
              ) && (
                <Card className="p-4">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Package className="h-5 w-5 text-primary" />
                        Products & nGrams
                      </CardTitle>
                      <DraggableVideoPlayer
                        videos={pageVideos.products}
                        className="flex"
                        buttonText="Video Guide"
                      />
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <p className="text-sm text-muted-foreground mb-3">
                      Analyze your product performance and discover nGram
                      patterns. Here, you can:
                    </p>
                    <ul className="list-disc list-inside text-muted-foreground space-y-1 text-xs">
                      <li>View product-level performance metrics and trends</li>
                      <li>
                        Identify top-performing and underperforming products
                      </li>
                      <li>Analyze nGram patterns in your search terms</li>
                      <li>Optimize product targeting and bidding strategies</li>
                    </ul>
                  </CardContent>
                </Card>
              )}

              {/* Budget Analysis Page Guide */}
              <Card className="p-4">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <BarChart3 className="h-5 w-5 text-primary" />
                      Budget Analysis
                    </CardTitle>
                    <DraggableVideoPlayer
                      videos={pageVideos.analysis}
                      className="flex"
                      buttonText="Video Guide"
                    />
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-sm text-muted-foreground mb-3">
                    Provides detailed insights into your campaign performance
                    and budget optimization. Here, you can:
                  </p>
                  <ul className="list-disc list-inside text-muted-foreground space-y-1 text-xs">
                    <li>
                      View key metrics like impressions, clicks, and conversions
                    </li>
                    <li>
                      Use graphs to visualize performance trends over time
                    </li>
                    <li>Compare current performance with previous periods</li>
                    <li>Receive AI-driven budget optimization suggestions</li>
                  </ul>
                </CardContent>
              </Card>

              {/* Trends Page Guide */}
              <Card className="p-4">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-primary" />
                      Trends & PMax Channels
                    </CardTitle>
                    <DraggableVideoPlayer
                      videos={pageVideos.trends}
                      className="flex"
                      buttonText="Video Guide"
                    />
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-sm text-muted-foreground mb-3">
                    Track Performance Max metrics across all channels in
                    real-time. Features include:
                  </p>
                  <ul className="list-disc list-inside text-muted-foreground space-y-1 text-xs">
                    <li>
                      Monitor performance trends across different time periods
                    </li>
                    <li>Analyze Performance Max channel distribution</li>
                    <li>Identify seasonal patterns and opportunities</li>
                    <li>Compare channel performance and allocation</li>
                  </ul>
                </CardContent>
              </Card>

              {/* Keywords Page Guide */}
              <Card className="p-4">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Search className="h-5 w-5 text-primary" />
                      Keywords & Search Terms
                    </CardTitle>
                    <DraggableVideoPlayer
                      videos={pageVideos.keywords}
                      className="flex"
                      buttonText="Video Guide"
                    />
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-sm text-muted-foreground mb-3">
                    Analyze search terms and build better negative keyword
                    lists. Here, you can:
                  </p>
                  <ul className="list-disc list-inside text-muted-foreground space-y-1 text-xs">
                    <li>Review all search terms triggering your ads</li>
                    <li>
                      Identify high-performing and irrelevant search terms
                    </li>
                    <li>Build comprehensive negative keyword lists</li>
                    <li>Analyze nGram patterns for better keyword targeting</li>
                  </ul>
                </CardContent>
              </Card>

              {/* Placements Page Guide */}
              <Card className="p-4">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Map className="h-5 w-5 text-primary" />
                      PMax Placements
                    </CardTitle>
                    <DraggableVideoPlayer
                      videos={pageVideos.placements}
                      className="flex"
                      buttonText="Video Guide"
                    />
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-sm text-muted-foreground mb-3">
                    Track and optimize your campaign placements across the
                    network. Features include:
                  </p>
                  <ul className="list-disc list-inside text-muted-foreground space-y-1 text-xs">
                    <li>View where your Performance Max ads are appearing</li>
                    <li>Analyze placement performance and costs</li>
                    <li>Identify top-performing and problematic placements</li>
                    <li>Optimize placement targeting and exclusions</li>
                  </ul>
                </CardContent>
              </Card>

              {/* Profit Curve Page Guide */}
              <Card className="p-4">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Target className="h-5 w-5 text-primary" />
                      Profit Curve
                    </CardTitle>
                    <DraggableVideoPlayer
                      videos={pageVideos.curve}
                      className="flex"
                      buttonText="Video Guide"
                    />
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-sm text-muted-foreground mb-3">
                    Visualize and optimize your profit curves for maximum ROI.
                    Features include:
                  </p>
                  <ul className="list-disc list-inside text-muted-foreground space-y-1 text-xs">
                    <li>
                      Graphical representation of profit against various metrics
                    </li>
                    <li>
                      Break-even analysis to identify revenue and cost balance
                      points
                    </li>
                    <li>Scenario testing to simulate different outcomes</li>
                    <li>Find your optimal CPA and budget allocation</li>
                  </ul>
                </CardContent>
              </Card>

              {/* Hourly Trends Page Guide */}
              <Card className="p-4">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Clock className="h-5 w-5 text-primary" />
                      Hourly Trends
                    </CardTitle>
                    <DraggableVideoPlayer
                      videos={pageVideos.hourly}
                      className="flex"
                      buttonText="Video Guide"
                    />
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-sm text-muted-foreground mb-3">
                    Analyze performance patterns throughout the day. Here, you
                    can:
                  </p>
                  <ul className="list-disc list-inside text-muted-foreground space-y-1 text-xs">
                    <li>View hourly performance breakdowns</li>
                    <li>Identify peak performance hours</li>
                    <li>Optimize bid adjustments for different time periods</li>
                    <li>Understand customer behavior patterns</li>
                  </ul>
                </CardContent>
              </Card>

              {/* Assets Page Guide */}
              <Card className="p-4">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Lightbulb className="h-5 w-5 text-primary" />
                      Asset Performance
                    </CardTitle>
                    <DraggableVideoPlayer
                      videos={pageVideos.assets}
                      className="flex"
                      buttonText="Video Guide"
                    />
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-sm text-muted-foreground mb-3">
                    Track and optimize your ad assets performance. Features
                    include:
                  </p>
                  <ul className="list-disc list-inside text-muted-foreground space-y-1 text-xs">
                    <li>
                      Analyze performance of headlines, descriptions, and images
                    </li>
                    <li>Identify top-performing creative assets</li>
                    <li>Optimize asset combinations for better results</li>
                    <li>Track asset approval status and recommendations</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* Common Use Cases */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Common Use Cases</h2>
            <p className="text-lg mb-3">
              Here are some common tasks you can perform with{" "}
              {brandConfig.appName}:
            </p>
            <ul className="list-disc list-inside text-lg space-y-1">
              <li>
                Optimize campaign budgets based on performance analysis and
                profit curves
              </li>
              <li>
                Analyze performance trends to make data-driven bidding decisions
              </li>
              <li>
                Build comprehensive negative keyword lists from search term
                analysis
              </li>
              <li>Identify and exclude poor-performing placements</li>
              <li>
                Optimize ad scheduling based on hourly performance patterns
              </li>
              <li>Improve creative performance through asset analysis</li>
            </ul>
          </section>

          {/* Troubleshooting */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Support</h2>
            <p className="text-lg">
              If you encounter any issues or need assistance, please contact me
              at{" "}
              <a
                href={`mailto:${brandConfig.company.supportEmail}`}
                className="text-primary hover:underline"
              >
                {brandConfig.company.supportEmail}
              </a>
              {brandConfig.support.helpUrl &&
                brandConfig.support.helpUrl !==
                  brandConfig.company.supportEmail && (
                  <>
                    {" "}
                    or visit the support page in Circle:{" "}
                    <a
                      href={brandConfig.support.helpUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      {brandConfig.support.helpText}
                    </a>
                  </>
                )}
              .
            </p>
          </section>
        </div>

        {/* Sidebar Area - Only show for default app */}
        {showSidebar && (
          <div className="lg:w-1/4 space-y-6">
            {/* Documentation Card */}
            <Card className="bg-gradient-to-br from-slate-50 to-gray-50 border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-slate-900">
                  <Book className="h-5 w-5 text-slate-600" />
                  Documentation
                </CardTitle>
                <CardDescription>
                  Guides for AI discovery, llms.txt, and agency tools.
                </CardDescription>
              </CardHeader>
              <CardFooter>
                <Button variant="outline" size="sm" asChild className="w-full">
                  <Link to="/documentation">
                    View Docs <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>

            {/* Automation Evolution Card */}
            <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-blue-900">
                  <Zap className="h-5 w-5 text-blue-600" />
                  Ads Automation Stages
                </CardTitle>
                <CardDescription>
                  Understand the evolution of Ads automation.
                </CardDescription>
              </CardHeader>
              <CardFooter>
                <Button variant="outline" size="sm" asChild className="w-full">
                  <Link to="/automation">
                    Explore Stages <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>

            {/* Why Card */}
            <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-green-900">
                  <HelpingHand className="h-5 w-5 text-green-600" />
                  Why {brandConfig.appName}?
                </CardTitle>
                <CardDescription>
                  Understand the vision behind the tool.
                </CardDescription>
              </CardHeader>
              <CardFooter>
                <Button
                  variant="outline"
                  size="sm"
                  asChild
                  className="w-full"
                  onClick={() => trackEvent(ANALYTICS_EVENTS.HELP.STORY_VIEW())}
                >
                  <Link to="/why">
                    Read the Story <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>

            {/* Scary Script Card */}
            {brandConfig.features.scaryScripts && (
              <Card className="bg-gradient-to-br from-orange-50 to-amber-50 border-orange-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-orange-900">
                    <Lightbulb className="h-5 w-5 text-orange-600" />
                    Scary Script Challenge
                  </CardTitle>
                  <CardDescription>
                    Boost your confidence with automation in 14 mins.
                  </CardDescription>
                </CardHeader>
                <CardFooter>
                  <Button
                    variant="outline"
                    size="sm"
                    asChild
                    className="w-full"
                    onClick={() =>
                      trackEvent(ANALYTICS_EVENTS.HELP.CHALLENGE_START())
                    }
                  >
                    <Link to="/scary">
                      Start the Challenge{" "}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            )}

            {/* Profit Curve Card */}
            <Card className="bg-gradient-to-br from-purple-50 to-violet-50 border-purple-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-purple-900">
                  <BarChart3 className="h-5 w-5 text-purple-600" />
                  Profit Curve Story
                </CardTitle>
                <CardDescription>
                  Learn interactively how to find your optimal CPA.
                </CardDescription>
              </CardHeader>
              <CardFooter>
                <Button
                  variant="outline"
                  size="sm"
                  asChild
                  className="w-full"
                  onClick={() =>
                    trackEvent(ANALYTICS_EVENTS.HELP.CURVE_STORY())
                  }
                >
                  <Link to="/story">
                    Explore the Story <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>

            {/* 20 Minutes a Week Card */}
            <Card className="bg-gradient-to-br from-teal-50 to-cyan-50 border-teal-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-teal-900">
                  <Clock className="h-5 w-5 text-teal-600" />
                  20 Minutes a Week
                </CardTitle>
                <CardDescription>
                  Calculate your AI automation time ROI and see the compound
                  effect.
                </CardDescription>
              </CardHeader>
              <CardFooter>
                <Button variant="outline" size="sm" asChild className="w-full">
                  <Link to="/20">
                    Calculate Your ROI <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>

            {/* CPD Card */}
            {brandConfig.features.salesPages && (
              <Card className="bg-gradient-to-br from-pink-50 to-rose-50 border-pink-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-pink-900">
                    <GraduationCap className="h-5 w-5 text-pink-600" />
                    CPD: Level Up
                  </CardTitle>
                  <CardDescription>
                    Explore career development opportunities.
                  </CardDescription>
                </CardHeader>
                <CardFooter>
                  <Button
                    variant="outline"
                    size="sm"
                    asChild
                    className="w-full"
                  >
                    <Link to="/CPD">
                      View CPD <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
